import { ReactNode, useEffect } from 'react';
import { loadAnalytics } from 'modules/analytics';
import { FeatureFlag, useFeatureFlags } from 'modules/unleash';
import { logError } from 'utils/datadog';
import { env } from '../../../constants';
import { initializeConsentManager } from '../utils';

export const ConsentManager = ({ children }: { children: ReactNode }) => {
  const { features, isLoading: isUnleashLoading } = useFeatureFlags();

  const isUserConsentEnabled =
    features?.[FeatureFlag.IS_USERCENTRICS_UI_ENABLED].enabled;

  useEffect(() => {
    if (!features || isUnleashLoading) {
      return;
    }
    if (!isUserConsentEnabled) {
      void loadAnalytics();
      return;
    }

    let cancelled = false;

    // Idempotent: safe to run on every effect pass. If the SDK is already up it wires the
    // consent handling directly; otherwise it (re-)arms the one-shot init listener. The
    // re-arm matters: a previous effect run's cleanup may have stripped the listener
    // before the SDK ever fired it, which used to kill consent init silently.
    const setupConsentManager = async () => {
      try {
        const isInitialized = await window.__ucCmp?.isInitialized();
        if (cancelled) {
          return;
        }
        if (isInitialized) {
          initializeConsentManager();
        } else {
          // Re-registering the same listener reference is a no-op per spec, so this
          // cannot stack duplicate handlers.
          window.addEventListener(
            'UC_UI_INITIALIZED',
            initializeConsentManager,
            { once: true }
          );
        }
      } catch (error) {
        logError('Error initializing CMP', error);
      }
    };

    if (document.getElementById('usercentrics-cmp')) {
      // Script already injected by an earlier run/remount — just (re-)ensure setup.
      void setupConsentManager();
    } else {
      const script = document.createElement('script');
      script.id = 'usercentrics-cmp';
      script.dataset.settingsId = env.VITE_USERCENTRIC_KEY;
      script.src = 'https://web.cmp.usercentrics.eu/ui/loader.js';
      script.async = true;
      // The CMP CDN serves Access-Control-Allow-Origin: * — with crossorigin, errors the
      // SDK throws reach window.onerror with real messages instead of opaque "Script error.".
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        void setupConsentManager();
      };
      script.onerror = () => {
        logError('Failed to load the Usercentrics script.');
      };
      document.body.appendChild(script);
    }

    return () => {
      cancelled = true;
      window.removeEventListener('UC_UI_INITIALIZED', initializeConsentManager);
    };
  }, [features, isUserConsentEnabled, isUnleashLoading]);

  return <>{children}</>;
};
